import React, { useState, useEffect } from 'react';
import BottomSheetModal from "../../elements/BottomSheetModal";
import Input from "../../form/Input";
import Card from "../../../assets/images/american-express.png";
import Button from "../../form/Button";
import Image from "../../elements/Image";
import QuantityInput from "../../form/QuantityInput";
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from '../../../pages/components/stripe/CheckoutForm';

const PayForYourItem = ({ 
  isOpen, 
  onClose, 
  total, 
  selectedTip,
  stripePromise,
  tableNumber,
  orderId,
  customTip = "",
  paymentType,
  previousTransactions = [],
  orderItems = [],
  paymentData = {}
}) => {
  const tipAmount = customTip ? Number(customTip) : (selectedTip || 0);
  
  const [items, setItems] = useState([]);
  const [selectedItemsTotal, setSelectedItemsTotal] = useState(0);

  // Calculate already paid items from paymentData by matching product AND variant
  const getPaidQuantity = (productId, variantId) => {
    if (!paymentData?.splits || !Array.isArray(paymentData.splits)) return 0;    
    let paidQty = 0;    
    paymentData.splits.forEach(split => {
      if (split.items && Array.isArray(split.items)) {
        split.items.forEach(paidItem => {
          // Match by both product AND variant
          if (paidItem.product === productId && paidItem.variant === variantId) {
            paidQty += Number(paidItem.qty || 0);
          }
        });
      }
    });    
    return paidQty;
  };

  // Initialize items with remaining quantities
  useEffect(() => {
    if (orderItems && orderItems.length > 0) {
      const itemsWithRemaining = orderItems.map(item => {
        const productId = item.product;
        const variantId = item.variant;
        const paidQty = getPaidQuantity(productId, variantId);        
        const originalQty = Number(item.qty || 0);
        const remainingQty = originalQty - paidQty;
        
        // Debug log
        // console.log(`Item: ${item.productName} (${item.variantName})`, {
        //   originalQty,
        //   paidQty,
        //   remainingQty,
        //   productId,
        //   variantId
        // });
        
        return {
          id: item._id || item.id,
          product: productId,
          variant: variantId,
          productName: item.productName || item.name,
          variantName: item.variantName || '',
          price: Number(item.price || 0),
          originalQty: originalQty,
          paidQty: paidQty,
          remainingQty: Math.max(0, remainingQty), // Ensure non-negative
          selectedQty: 0,
          maxQty: Math.max(0, remainingQty)
        };
      }).filter(item => item.remainingQty > 0); // Only show items with remaining quantity > 0

      //console.log('Filtered items with remaining qty:', itemsWithRemaining);

      setItems(prevItems => {
        const hasChanged = prevItems.length !== itemsWithRemaining.length || 
          prevItems.some((item, index) => 
            item.product !== itemsWithRemaining[index]?.product || 
            item.variant !== itemsWithRemaining[index]?.variant
          );        
        return hasChanged ? itemsWithRemaining : prevItems;
      });
    }
  }, [orderItems, paymentData]);
 
  // Calculate total for selected items
  useEffect(() => {
    const total = items.reduce((sum, item) => {
      return sum + (item.selectedQty * item.price);
    }, 0);
    setSelectedItemsTotal(total);
  }, [items]);

  const handleQtyChange = (id, newQty) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const validQty = Math.min(Math.max(0, newQty), item.maxQty);
          return { ...item, selectedQty: validQty };
        }
        return item;
      })
    );
  };

  // Calculate total paid from previous transactions
  const totalPaid = previousTransactions?.reduce((sum, txn) => 
    sum + (Number(txn.amount) || 0) + (Number(txn.tipAmount) || 0), 0
  ) || 0;

  // Calculate remaining due
  const remainingDue = total - totalPaid;
  const finalPayableAmount = selectedItemsTotal + tipAmount;
  // Check if any items are selected
  const hasSelectedItems = items.some(item => item.selectedQty > 0);
  // Check if all items are fully paid
  const allItemsPaid = items.length === 0;

  return (
    <>
      <BottomSheetModal isOpen={isOpen} onClose={onClose} bgclass={"p-0 xls:max-w-sm xls:mx-auto"}>
        <div className="h-[calc(100vh-150px)] overflow-y-auto pb-5">
          <div className="border-b border-slate-200 mb-3">
            <div className="px-5 mt-4">
              <h2 className="text-lg font-semibold text-[#232222] mb-1">Pay for your items</h2>
              <p className="text-[#9FA1A3] text-base mb-4 font-normal">
                Select the items you want to pay for and complete your payment securely.
              </p>
            </div>
          </div>

          {/* Show message if all items are paid */}
          {allItemsPaid ? (
            <div className="px-5 py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-check text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">All Items Paid</h3>
              <p className="text-sm text-gray-600">
                All items in this order have been paid for.
              </p>
            </div>
          ) : (
            <>
              {/* Items List */}
              <div className="px-5 mb-6 space-y-3">
                {items.map((item, index) => (
                  <div
                    key={`${item.product}-${item.variant}-${index}`}
                    className="border border-gray-200 rounded-lg p-3 bg-white"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0 pr-2">
                        <h4 className="text-sm font-semibold text-[#232222] truncate">
                          {item.productName}
                          {item.variantName && (
                            <span className="text-xs text-gray-500 ml-1">- {item.variantName}</span>
                          )}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          ${item.price.toFixed(2)} per item
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-xs text-gray-600">
                            Total ordered: {item.originalQty}
                          </p>
                          {item.paidQty > 0 && (
                            <p className="text-xs text-green-600 flex items-center gap-1">
                              <i className="fa-solid fa-check-circle"></i>
                              {item.paidQty} paid
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-semibold text-blue-600">
                          Available: {item.remainingQty}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex-shrink-0">
                        <QuantityInput
                          value={item.selectedQty}
                          onChange={(val) => handleQtyChange(item.id, val)}
                          min={0}
                          max={item.maxQty}
                        />
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Subtotal</p>
                        <p className="text-base font-bold text-[#232222]">
                          ${(item.selectedQty * item.price).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment Summary */}
              <div className="px-5 space-y-4">
                {/* Payment Breakdown */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {/* Items Total */}
                  <div className="flex items-center justify-between px-4 py-3 bg-[#F5F5F5] border-b border-slate-200">
                    <span className="text-[#232222] text-base font-normal">
                      Selected Items
                    </span>
                    <span className="text-[#232222] text-lg font-semibold">
                      ${selectedItemsTotal.toFixed(2)}
                    </span>
                  </div>

                  {/* Breakdown Section */}
                  <div className="p-4 space-y-3">
                    {/* Total Bill Amount */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5F6368] font-medium">
                        Total bill amount
                      </span>
                      <span className="text-base font-bold text-[#232222]">
                        ${total?.toFixed(2) || "0.00"}
                      </span>
                    </div>

                    {/* Already Paid */}
                    {totalPaid > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5F6368] font-medium flex items-center gap-1">
                          <i className="fa-solid fa-check-circle text-green-500 text-xs"></i>
                          Already paid
                        </span>
                        <span className="text-base font-bold text-green-600">
                          -${totalPaid.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Remaining Due */}
                    {totalPaid > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5F6368] font-medium">
                          Remaining due
                        </span>
                        <span className="text-base font-bold text-orange-600">
                          ${remainingDue.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Your Items Total */}
                    {selectedItemsTotal > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5F6368] font-medium flex items-center gap-1">
                          <span className="w-2 h-2 bg-blue-500 rounded-sm"></span>
                          Your items total
                        </span>
                        <span className="text-base font-bold text-[#1967D2]">
                          ${selectedItemsTotal.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Tip Amount */}
                    {tipAmount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5F6368] font-medium flex items-center gap-1">
                          <span>❤️</span>
                          Tip amount
                        </span>
                        <span className="text-base font-bold text-[#0F9D58]">
                          +${tipAmount.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Final Amount */}
                    <div className="bg-[#F8F9FA] rounded-lg px-3 py-2.5 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-[#232222]">
                          You are paying
                          {tipAmount > 0 && (
                            <span className="text-xs text-[#5F6368] font-normal"> (including tip)</span>
                          )}
                        </span>
                        <span className="text-lg font-bold text-[#0F9D58]">
                          ${finalPayableAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Notice */}
                {!hasSelectedItems && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                    <i className="fa-solid fa-info-circle text-blue-600 text-sm mt-0.5"></i>
                    <p className="text-xs text-blue-800">
                      Please select at least one item to proceed with payment
                    </p>
                  </div>
                )}

                {/* Payment Section */}
                {hasSelectedItems && (
                  <div className="rounded-xl border border-gray-200 px-3 shadow-sm pt-5 pb-3 bg-slate-50/30">
                    <Elements stripe={stripePromise}>
                      <CheckoutForm 
                        paymentType={paymentType} 
                        tableNumber={tableNumber}
                        orderId={orderId}
                        tipAmount={tipAmount}
                        customPayAmount={selectedItemsTotal}
                        selectedItems={items.filter(item => item.selectedQty > 0).map(item => ({
                          product: item.product,
                          variant: item.variant,
                          qty: item.selectedQty,
                          price: item.price,
                          subtotal: item.selectedQty * item.price                          
                        }))}
                      />
                    </Elements>

                    <p className="text-[10px] text-gray-500 mt-3 text-center flex items-center justify-center gap-1">
                      <i className="fa-solid fa-shield-check text-green-600"></i>
                      100% Secure payments
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </BottomSheetModal>
    </>
  );
};

export default PayForYourItem;